import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import React from "react";
import ReactDOM from "react-dom";
import styled, { css } from "styled-components";

import Style from "@client/utils/Style";

import SearchInput from "../SearchInput";
import arrowImage from "./images/arrow";
import checkmarkImage from "./images/checkmark.svg";

const Wrapper = styled.div`
  display: inline-block;
  position: relative;
`;
const SearchInputWrapper = styled.div``;
const LinkButton = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${props => (props.disabled ? "default" : "pointer")};
`;
type ListProps = {
  width?: string;
  padding?: number;
  customStyle?: any;
};
export const List = styled.div<ListProps>`
  position: absolute;
  z-index: 1001;
  padding: ${props => (props.padding != null ? props.padding : 8)}px;
  background: ${Style.palette.grayscale[1]};
  border-radius: 4px;
  border: 1px solid ${Style.palette.grayscale[0]};
  ${props =>
    props.width
      ? Style.exactWidth(props.width)
      : css`
          min-width: 132px;
          max-width: 160px;
        `}
  ${props => props.customStyle || ""}
`;
export const Tip = styled.div<{ borderColor?: React.CSSProperties["color"] }>`
  position: absolute;
  top: -6px;
  right: 11px;
  width: 10px;
  height: 10px;
  background: ${Style.palette.grayscale[1]};
  border-top: 1px solid
    ${props => props.borderColor || Style.palette.grayscale[0]};
  border-left: 1px solid
    ${props => props.borderColor || Style.palette.grayscale[0]};
  border-bottom: 1px solid transparent;
  border-right: 1px solid transparent;
  transform: rotate(45deg);
  transition: all ${Style.animations.swift};
`;
export const ListItems = styled.div<{ searchable?: boolean }>`
  max-height: 400px;
  overflow: auto;
  ${props => (props.searchable ? "margin-top: 8px;" : "")}
`;
export const ListItem = styled.div<{ selected?: boolean }>`
  padding-top: 13px;
  color: ${props =>
    props.selected ? Style.palette.primary : Style.palette.grayscale[4]};
  cursor: pointer;
  display: flex;
  align-items: center;

  &:first-child {
    padding-top: 0;
  }
`;
type ListItemLabelProps = {
  highlighted?: boolean;
  addMargin?: number;
  customStyle?: any;
};
export const ListItemLabel = styled.div<ListItemLabelProps>`
  word-break: break-all;
  word-break: break-word;
  ${props =>
    props.highlighted ? `font-weight: ${Style.fontWeights.medium};` : ""}
  ${props =>
    props.addMargin
      ? css`
          margin-left: ${props.addMargin}px;
        `
      : ""}
  ${props => props.customStyle}
`;
const Checkmark = styled.div<{ show?: boolean }>`
  ${Style.exactWidth("16px")}
  height: 16px;
  background: ${props =>
    props.show ? `url('${checkmarkImage}') center no-repeat` : "transparent"};
  margin-right: 8px;
`;
const Label = styled.div<{ secondary?: boolean }>`
  color: ${props =>
    props.secondary ? Style.palette.grayscale[4] : Style.palette.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Arrow = styled.div`
  width: 16px;
  height: 16px;
  margin-left: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${
    // $FlowIgnore
    (props: any) =>
      props.orientation === "right"
        ? css`
            transform: rotate(-90deg);
          `
        : ""
  }
  ${
    // $FlowIgnore
    (props: any) =>
      props.orientation === "left"
        ? css`
            transform: rotate(90deg);
          `
        : ""
  }
`;
const EmptySearch = styled.div`
  margin-top: 8px;
`;

type ItemType = {
  label: string;
  value: string;
  [prop: string]: any;
};
type Props = {
  selectedItem?: ItemType;
  items: ItemType[];
  onChange?: (item: ItemType) => void;
  highlightedItem?: string;
  className?: string;
  width?: string;
  selectItemLabel?: string;
  noItemsLabel?: string;
  listWidth?: string;
  searchable?: boolean;
  disabled?: boolean;
  secondary?: boolean;
  multipleSelection?: boolean;
  selectedItems?: string[];
  linkButtonStyle?: any;
  arrowImage?: (color: string) => string;
  noCheckmark?: boolean;
  itemStyle?: (item: ItemType) => string;
  style?: React.CSSProperties;
  labelStyle?: any;
  getLabel?: () => string;
};
type State = {
  showDropdownList: boolean;
  searchText: string;
};
@observer
class DropdownLink extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    selectItemLabel: "Select",
    noItemsLabel: "No items",
  };

  state: State = {
    showDropdownList: false,
    searchText: "",
  };

  itemMouseDown = false;
  labelRef: HTMLElement | null = null;
  listItemsRef: HTMLElement | null = null;
  listRef: HTMLElement | null = null;
  arrowRef: HTMLElement | null = null;
  tipRef: HTMLElement | null = null;
  searchInputWrapperRef: HTMLElement | null = null;

  get value() {
    return this.props.selectedItem ? this.props.selectedItem.value : null;
  }

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    this.setLabelWidth();
  }

  componentDidUpdate() {
    this.setLabelWidth();
    this.updateListPosition();
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
  }

  setLabelWidth() {
    const labelRef = this.labelRef;
    if (!labelRef) {
      return;
    }

    labelRef.style.width = "";
    if (!this.props.width) {
      return;
    }
    let width = parseInt(this.props.width, 10);
    if (!width) {
      return;
    }

    width -= 28;
    const labelWidth = labelRef.offsetWidth;
    if (labelWidth < width) {
      return;
    }

    labelRef.style.width = `${width}px`;
  }

  getFilteredItems(): ItemType[] {
    const items = this.props.items;
    return items.filter(
      item =>
        item.value.toLowerCase().indexOf(this.state.searchText.toLowerCase()) >
          -1 ||
        item.label.toLowerCase().indexOf(this.state.searchText.toLowerCase()) >
          -1,
    );
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ showDropdownList: false });
    }
  }

  handleButtonClick() {
    if (this.props.disabled) {
      return;
    }

    this.setState({ showDropdownList: !this.state.showDropdownList }, () => {
      this.scrollIntoView();
    });
  }

  handleItemClick(item: ItemType) {
    if (!this.props.multipleSelection) {
      this.setState({ showDropdownList: false });
    }

    if (this.props.onChange) {
      this.props.onChange(item);
    }
  }

  handleSearchTextChange(searchText: string) {
    this.setState({ searchText });
  }

  scrollIntoView() {
    const listItemsRef = this.listItemsRef;
    if (!this.listRef || !listItemsRef) {
      return;
    }

    const itemIndex = this.props.items.findIndex(i => i.value === this.value);
    if (itemIndex === -1 || !listItemsRef.children[itemIndex]) {
      return;
    }
    const child = listItemsRef.children[itemIndex] as HTMLElement;
    const parentNode = child.parentNode as HTMLElement | null;
    if (!parentNode) {
      return;
    }
    parentNode.scrollTop = child.offsetTop - parentNode.offsetTop;
  }

  updateListPosition() {
    const listRef = this.listRef;
    if (
      !this.state.showDropdownList ||
      !listRef ||
      !this.arrowRef ||
      !this.tipRef
    ) {
      return;
    }

    const listWidth = listRef.offsetWidth;
    const arrowWidth = this.arrowRef.offsetWidth;
    const arrowHeight = this.arrowRef.offsetHeight;
    const tipHeight = this.tipRef.offsetHeight;
    const tipOffset = 11;
    const arrowOffset = this.arrowRef.getBoundingClientRect();

    // If a modal is opened, body scroll is removed and body top is set to replicate scroll position
    let scrollOffset = 0;
    if (document.body && parseInt(document.body.style.top, 10) < 0) {
      scrollOffset = -parseInt(document.body && document.body.style.top, 10);
    }

    listRef.style.top = `${
      arrowOffset.top +
      (window.pageYOffset || scrollOffset) +
      arrowHeight +
      tipHeight
    }px`;
    listRef.style.left = `${
      arrowOffset.left + tipOffset + (arrowWidth - listWidth)
    }px`;
  }

  renderSearch() {
    if (!this.props.searchable) {
      return null;
    }

    return (
      <SearchInputWrapper
        ref={ref => {
          this.searchInputWrapperRef = ref;
        }}
        onMouseDown={() => {
          this.itemMouseDown = true;
        }}
        onMouseUp={() => {
          this.itemMouseDown = false;
        }}
      >
        <SearchInput
          alwaysOpen
          width="100%"
          onChange={text => {
            this.handleSearchTextChange(text);
          }}
          value={this.state.searchText}
        />
      </SearchInputWrapper>
    );
  }

  renderEmptySearch() {
    if (!this.state.searchText || this.getFilteredItems().length > 0) {
      return null;
    }

    return <EmptySearch>No items found</EmptySearch>;
  }

  renderItem(item: ItemType) {
    const highlighted =
      item.value !== this.value
        ? item.value === this.props.highlightedItem
        : false;
    const label =
      item.label ||
      item.value.toString().charAt(0).toUpperCase() +
        item.value.toString().substr(1);
    let selected;

    if (this.props.multipleSelection && this.props.selectedItems) {
      selected = Boolean(this.props.selectedItems.find(i => i === item.value));
    } else {
      selected = item.value === this.value;
    }

    return (
      <ListItem
        key={item.label || item.value}
        onMouseDown={() => {
          this.itemMouseDown = true;
        }}
        onMouseUp={() => {
          this.itemMouseDown = false;
        }}
        onClick={() => {
          this.handleItemClick(item);
        }}
        selected={selected}
      >
        {!this.props.noCheckmark ? <Checkmark show={selected} /> : null}
        <ListItemLabel
          highlighted={highlighted}
          addMargin={this.props.noCheckmark ? 8 : 0}
          customStyle={this.props.itemStyle ? this.props.itemStyle(item) : ""}
        >
          {label}
        </ListItemLabel>
      </ListItem>
    );
  }

  renderListItems() {
    if (this.state.searchText && this.getFilteredItems().length === 0) {
      return null;
    }

    return (
      <ListItems
        ref={ref => {
          this.listItemsRef = ref;
        }}
        searchable={this.props.searchable}
      >
        {this.getFilteredItems().map(item => {
          return this.renderItem(item);
        })}
      </ListItems>
    );
  }

  renderList() {
    if (
      !this.props.items ||
      this.props.items.length === 0 ||
      !this.state.showDropdownList
    ) {
      return null;
    }

    const body: any = document.body;
    return ReactDOM.createPortal(
      <List
        ref={list => {
          this.listRef = list;
        }}
        width={this.props.listWidth}
      >
        <Tip
          ref={ref => {
            this.tipRef = ref;
          }}
        />
        {this.renderSearch()}
        {this.renderEmptySearch()}
        {this.renderListItems()}
      </List>,
      body,
    );
  }

  render() {
    const renderLabel = () => {
      if (this.props.getLabel) {
        return this.props.getLabel();
      }
      if (this.props.items && this.props.items.length && this.value) {
        const item = this.props.items.find(i => i.value === this.value);
        if (item && item.label) {
          return item.label;
        }
      }
      if (!this.props.items || this.props.items.length === 0) {
        return this.props.noItemsLabel;
      }
      return this.props.selectItemLabel;
    };

    const arrowImageFunc = this.props.arrowImage || arrowImage;

    return (
      <Wrapper
        className={this.props.className}
        onMouseDown={() => {
          this.itemMouseDown = true;
        }}
        onMouseUp={() => {
          this.itemMouseDown = false;
        }}
        style={this.props.style}
      >
        <LinkButton
          onClick={() => this.handleButtonClick()}
          disabled={this.props.disabled}
          style={this.props.linkButtonStyle}
        >
          <Label
            secondary={this.props.secondary}
            ref={label => {
              this.labelRef = label;
            }}
            style={this.props.labelStyle}
          >
            {renderLabel()}
          </Label>
          <Arrow
            ref={arrow => {
              this.arrowRef = arrow;
            }}
            dangerouslySetInnerHTML={{
              __html: arrowImageFunc(
                this.props.secondary
                  ? Style.palette.grayscale[3]
                  : Style.palette.primary,
              ),
            }}
          />
        </LinkButton>
        {this.renderList()}
      </Wrapper>
    );
  }
}

export default DropdownLink;
